import { t } from 'i18next'
import { ToastEventPayload } from '~/hooks/toast/useToastAlerts'
import {
  NotificationDomain,
  NotificationPayload,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'

export enum WebphoneOperation {
  CallDeclined = 'call_declined',
  CallEnded = 'call_ended',
  CallHeld = 'call_held',
  CallParked = 'call_parked',
  CallTargetUndialable = 'call_target_undialable',
  CallTransferred = 'call_transferred',
  CallUnavailable = 'call_unavailable',
}

export type WebphoneOperationContext = {
  [WebphoneOperation.CallDeclined]: string
  [WebphoneOperation.CallEnded]: string
  [WebphoneOperation.CallHeld]: string
  [WebphoneOperation.CallParked]: { callName: string; parkingLotName: string }
  [WebphoneOperation.CallTargetUndialable]: { target: string }
  [WebphoneOperation.CallTransferred]: {
    callName: string
    transferToName: string
  }
  [WebphoneOperation.CallUnavailable]: void
}

const getCallDeclinedToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const context =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallDeclined]

  switch (payload.disposition) {
    case 'red.solid':
      title = t('call.toast.callDeclined', { context })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    description: '',
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallEndedToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const context =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallEnded]

  switch (payload.disposition) {
    case 'red.solid':
      title = t('call.toast.callEnded', { context })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallHeldToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const context =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallHeld]

  switch (payload.disposition) {
    case 'green.solid':
      title = t('call.toast.callHeld', { context })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallParkedToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const { callName, parkingLotName } =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallParked]

  switch (payload.disposition) {
    case 'green.solid':
      title = t('call.toast.callParked', { callName, parkingLotName })
      break
    case 'red.solid':
      title = t('call.toast.callParkFailed', { callName, parkingLotName })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallTransferredToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const { callName, transferToName } =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallTransferred]

  switch (payload.disposition) {
    case 'green.solid':
      title = t('call.toast.callTransferred', { callName, transferToName })
      break
    case 'red.solid':
      title = t('call.toast.callTransferFailed', { callName, transferToName })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallTargetUndialableToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  const { target } =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallTargetUndialable]

  if (payload.disposition !== 'red.solid') {
    return
  }

  return {
    title: t('call.toast.callTargetUndialable', { target }),
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallUnavailableToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true

  switch (payload.disposition) {
    case 'red.solid':
      title = t('call.toast.callUnavailable')
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

export const getWebphoneToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  switch (payload.operation) {
    case WebphoneOperation.CallDeclined:
      return getCallDeclinedToastEvent(payload)
    case WebphoneOperation.CallEnded:
      return getCallEndedToastEvent(payload)
    case WebphoneOperation.CallHeld:
      return getCallHeldToastEvent(payload)
    case WebphoneOperation.CallParked:
      return getCallParkedToastEvent(payload)
    case WebphoneOperation.CallTargetUndialable:
      return getCallTargetUndialableToastEvent(payload)
    case WebphoneOperation.CallTransferred:
      return getCallTransferredToastEvent(payload)
    case WebphoneOperation.CallUnavailable:
      return getCallUnavailableToastEvent(payload)
    default:
      return null
  }
}
